import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemText,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {Box} from '@mui/system';
import {format} from '@react-input/number-format';

import useStaffUserLookup from "@components/common/hooks/useStaffUserLookup";
import EventDemographics from "@components/ContentComponents/Events/Demographics/EventDemographics";
import {evaluationToolOptions, NILL, participantNeeds} from '@lib/constants';
import {formatDateTime} from '@lib/utils';
import {shouldCollectDemographics} from '../../eventFunctions';

const CompleteRow = ({ n, name, colA, colB, colC }) => (
  <TableRow id={`${name}-row`}>
    <TableCell id={`${name}-colA`}>{`${n}. ${colA}`}</TableCell>
    <TableCell id={`${name}-colB`}>{colB}</TableCell>
    <TableCell id={`${name}-colC`}>{colC}</TableCell>
  </TableRow>
);

const TableComp = styled(Table)(({ theme }) => ({
  marginBottom: '32px',
  '& .MuiTableCell-root': {
    fontSize: theme.typography.pxToRem(16),
    padding: '10px 12px',
    '&.MuiTableCell-head': {
      padding: '16px',
    }
  }
}));

const TableHeadRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    backgroundColor: '#f4f4f4',
    '&:first-of-type': {
      fontWeight: 'bold',
    },
  },
});

/**
 * Displays values of a field when the form is in read-only mode.
 */
const ReadOnlyField = ({label, value, extra = '', fullWidth}) => (
  <>
    <Typography variant="body1" sx={{fontWeight: 600}}>{label}</Typography>
    {extra}
    <Typography variant="body1" sx={{
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      mt: 2,
      mb: 2,
      maxWidth: fullWidth ? '100%' : '150px',
      minHeight: 20,
    }}>
      {value}
    </Typography>
  </>
);

/**
 * Displays values of a field when the form is in read-only mode.
 */
const ArrayField = ({label, values}) => (
  <>
    <Typography variant="body1" sx={{fontWeight: 600}}>{label}</Typography>
    <ul>
      {values.map(v => <li key={v}>{v}</li>)}
    </ul>
  </>
);

/**
 * Displays values of a field when the form is in read-only mode.
 */
const ReadOnlyCell = ({value}) => (
  <Typography variant="body1">{value}</Typography>
);

const CompleteSessionDetail = ({
  session,
  previous_sessions,
  previous_participant_actions
}) => {

  const [demographicsExpanded, setDemographicsExpanded] = useState(false);

  const theme = useTheme();
  const { getUserName } = useStaffUserLookup();

  const completed = session.completed;

  const hasPreviousSessions = previous_sessions?.length > 0;
  const hasPreviousActions = previous_participant_actions?.length > 0;

  const hasDemonstration = session.demonstration || completed.demonstration;

  const PreviousActions = () => {
    if (!hasPreviousActions && !hasPreviousSessions) {
      return null;
    }
    return (
      <Box sx={{
        backgroundColor: theme.palette.lightblue.main,
        padding: '10px 16px',
        marginLeft: '16px',
      }}>
        <Typography variant="h6" fontFamily="SourceSans">Actions taken so far:</Typography>
        <List sx={{
          padding: 0,
          '& .MuiListItem-root': {
            padding: 0,
          },
          '& .MuiListItemText-root': {
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'SourceSans',
          }
        }}>
          {previous_participant_actions.map((action, i) => (
            <ListItem key={`action-${i}`}>
              <ListItemText primary={action} sx={{fontStyle: 'italic'}} />
            </ListItem>
          ))}
          {!hasPreviousActions && (
            <ListItem>
              <ListItemText primary="No actions found" />
            </ListItem>
          )}
        </List>
      </Box>
    );
  }

  const isClass = session.event.event_type === 'CLASS';

  const rows = [];

  const topRows = useMemo(() => {
    const tRows = [];
    tRows.push((i) =>
      <ReadOnlyField
        name="as_planned"
        label={`${i}. Overall, did this session go the way you thought it would?`}
        value={completed.as_planned ? 'Yes' : 'No'}
      />
    );

    if (!completed.as_planned) {
      const label = 'Please share a bit more about why you selected "No" above.'
      tRows.push((i) =>
        <ReadOnlyField
          name="as_planned_detail"
          label={`${i}. ${label}`}
          value={completed.as_planned_detail}
          fullWidth
        />
      );
    }
    return tRows;
  }, [completed]);

  const needsAndActions = [];
  const needsLabel1 = 'Add or remove needs to reflect what you have learned about this group so far.';
  const needsLabel2 = hasPreviousSessions ?
    'Write any new actions you have taken to meet the specific needs of this group.' :
    'What actions have you taken to meet the specific needs of this group?';

  needsAndActions.push((i) => (
    <ArrayField
      name="participant_needs"
      label={`${i}. ${needsLabel1}`}
      values={completed.participant_needs.map((need) => need === 'other' ?
        `Other: ${completed.participant_needs_other}` :
        participantNeeds.find(pn => pn.id === need)?.label)}
      fullWidth
    />
  ));

  needsAndActions.push((i) => (
    <ReadOnlyField
      name="participant_actions"
      label={`${i}. ${needsLabel2}`}
      extra={<PreviousActions />}
      value={completed.participant_actions}
      fullWidth
    />
  ));

  const participantFieldLabel = useMemo(() => {
    if (!session?.event) {
      return '';
    }
    if (session.event.event_type === 'BOOTH') {
      return 'How many participants did you actively engage during this booth?';
    }
    switch (session.event.curriculum?.audience) {
      case 'Youth':
        return 'How many youth participants attended this session?';
      case 'Adults':
        return 'How many adult participants attended this session?';
      case 'Family':
        return 'How many adult and youth participants attended this session? Only include youth who actively participated in the session.';
      default:
        return 'How many participants attended this session?';
    }
  }, [session.event]);

  // If the event is a type we collect demographics for, and demographics data has been entered for the
  // event, show the demographics data table.
  const showDemographicsFields = (shouldCollectDemographics(session.event) && !!session.event.demographics);

  const participantCountFields = [];

  participantCountFields.push((i) => (
    <ReadOnlyField
      name="participant_count"
      label={`${i}. ${participantFieldLabel}`}
      value={completed.participant_count}
    />
  ));

  if (showDemographicsFields) {
    participantCountFields.push((i) => (
      <Accordion
        sx={{mb: 2}}
        expanded={demographicsExpanded}
        onChange={(ev, isExpanded) => setDemographicsExpanded(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="demographics-content"
          id="demographics-header"
        >
          <Typography variant="h3" sx={{fontSize: '1rem'}}>View/Edit Demographics</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EventDemographics event={session.event} />
        </AccordionDetails>
      </Accordion>
    ));
  }

  participantCountFields.push((i) => (
    <ReadOnlyField
      name="participant_has_story"
      label={`${i}. Do you have a participant success story to share from this session?`}
      value={completed.participant_has_story ? 'Yes' : 'No'}
    />
  ));

  if (completed.participant_has_story) {
    participantCountFields.push((i) => (
      <ReadOnlyField
        name="participant_permission"
        label={`${i}. Did you receive verbal permission from the participant to share this story?`}
        value={completed.participant_permission ? 'Yes' : 'No'}
      />
    ));

    participantCountFields.push((i) => (
      <ArrayField
        name="participant_behaviors"
        label={`${i}. Select all Key Behaviors related to this story:`}
        values={completed.participant_behaviors.efnep}
      />
    ));

    participantCountFields.push((i) => (
      <ReadOnlyField
        name="participant_story"
        label={`${i}. Please share the participant story below.`}
        value={completed.participant_story}
        fullWidth
      />
    ));
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-lesson`}
      name="lesson"
      colA={isClass ? 'Lesson Taught' : 'Booth Topic'}
      colB={session.lesson.lesson_name || session.lesson.booth_topic}
      colC={
        <ReadOnlyCell name="lesson" value={completed.lesson.lesson_name || completed.lesson.booth_topic} />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-start`}
      name="start"
      colA="Start Date & Time"
      colB={formatDateTime(session.start)}
      colC={<ReadOnlyCell name="start" value={formatDateTime(completed.start)} />}
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-end`}
      name="end"
      colA="End Date & Time"
      colB={formatDateTime(session.end)}
      colC={<ReadOnlyCell name="end" value={formatDateTime(completed.end)} />
      }
    />
  );

  if (isClass) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-delivery_method`}
        name="delivery_method"
        colA="Delivery Method"
        colB={session.delivery_method?.join(', ')}
        colC={<ReadOnlyCell name="delivery_method" value={completed.delivery_method?.join(', ')} />}
      />
    );
  }

  if (session.delivery_method?.includes('In Person')) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-delivery_location`}
        name="delivery_location"
        colA="Session Delivery Site"
        colB={session.delivery_location || NILL}
        colC={<ReadOnlyCell name="delivery_location" value={completed.delivery_location} />}
      />
    );
  }

  if (session.delivery_method?.includes('Virtual/Live Online')) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-delivery_url`}
        name="delivery_url"
        colA="Session URL/Link"
        colB={<Typography variant="body1" sx={{wordBreak: "break-all"}}>{session.delivery_url || NILL}</Typography>}
        colC={<Typography variant="body1" sx={{wordBreak: "break-all"}}>{completed.delivery_url}</Typography>}
      />
    );
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-delivery_language`}
      name="delivery_language"
      colA="Session Delivery Language(s)"
      colB={session.delivery_language?.join(', ')}
      colC={<ReadOnlyCell name="delivery_language" value={completed.delivery_language?.join(', ')} />}
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-material_language`}
      name="material_language"
      colA="Participant Material Language(s)"
      colB={session.material_language?.join(', ')}
      colC={<ReadOnlyCell name="material_language" value={completed.material_language?.join(', ')} />}
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-demonstration`}
      name="demonstration"
      colA="Food Demonstration/Taste Test Included"
      colB={session.demonstration ? 'Yes' : 'No'}
      colC={<ReadOnlyCell name="delivery_url" value={completed.demonstration ? 'Yes' : 'No'} />}
    />
  );

  if (hasDemonstration) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-recipes`}
        name="recipes"
        colA="Recipe(s) Used"
        colB={session?.recipes || 'Not Applicable'}
        colC={<ReadOnlyCell name="delivery_url" value={completed.recipes} />}
      />
    );
  }

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-collaborators`}
      name="collaborators"
      colA="Collaborators"
      colB={session.collaborators.length
        ? session.collaborators.map(c => getUserName(c.email)).sort((a, b) => a.localeCompare(b)).join(', ')
        : 'None'}
      colC={
        <ReadOnlyCell
          name="collaborators"
          value={completed.collaborators.length
            ? completed.collaborators.map(c => getUserName(c.email)).sort((a, b) => a.localeCompare(b)).join(', ')
            : 'None'}
        />
      }
    />
  );

  rows.push(
    (i) => <CompleteRow
      n={i}
      key={`row-donations_received`}
      name="donations_received"
      colA="External Donations Received"
      colB="Not Applicable"
      colC={<ReadOnlyCell name="donations_received" value={completed.donations_received ? 'Yes' : 'No'} />}
    />
  );

  if (completed.donations_received) {
    const donations_total = format(completed.donations_total || 0, {
      format: 'currency',
      currency: 'USD',
      locales: 'en',
    });

    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-donations_total`}
        name="donations_total"
        colA="Total Value of Donations Received"
        colB="Not Applicable"
        colC={<ReadOnlyCell name="donations_total" value={donations_total} />}
      />
    );
  }

  if (isClass) {
    rows.push(
      (i) => <CompleteRow
        n={i}
        key={`row-evaluation_tool`}
        name="evaluation_tool"
        colA="Evaluation Tool Distributed"
        colB="Not Applicable"
        colC={
          <ReadOnlyCell
            name="evaluation_tool"
            value={evaluationToolOptions.find(opt => opt.id === completed.evaluation_tool)?.label}
          />
        }
      />
    );
  }

  let rowCount = 0;

  const iterate = (rowItems, rowWrap = false, noBorder = false) => rowItems.map((row) => {
    rowCount += 1;
    const element = row(rowCount);
    if (rowWrap) {
      return (
        <TableRow key={`table-row-${element.props.name}`} id={`table-row-${element.props.name}`}>
          <TableCell sx={noBorder ? { borderBottom: 'none' } : {}}>
            {element}
          </TableCell>
        </TableRow>
      );
    }
    return element;
  });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <TableComp>
          <TableBody>
            {iterate(topRows, true, true)}
          </TableBody>
        </TableComp>

        <TableComp>
          <TableHead>
            <TableHeadRow>
              <TableCell>Confirm Group Needs Identified and Actions Taken To Address Needs</TableCell>
            </TableHeadRow>
          </TableHead>
          <TableBody sx={{
            '& .MuiTableCell-root:first-of-type': { paddingTop: '24px' },
          }}>
            {iterate(needsAndActions, true, true)}
          </TableBody>
        </TableComp>

        <TableComp>
          <TableHead>
            <TableHeadRow>
              <TableCell>Confirm Participant Details</TableCell>
            </TableHeadRow>
          </TableHead>
          <TableBody sx={{
            '& .MuiTableCell-root:first-of-type': { paddingTop: '24px' },
          }}>
            {iterate(participantCountFields, true, true)}
          </TableBody>
        </TableComp>

        <TableComp>
          <TableHead>
            <TableHeadRow>
              <TableCell>Confirm Session Details</TableCell>
              <TableCell>Planned</TableCell>
              <TableCell>Actual</TableCell>
            </TableHeadRow>
          </TableHead>
          <TableBody sx={{ '& .MuiTableCell-root:last-of-type': { width: '40%' } }}>
            {iterate(rows, false)}
          </TableBody>
        </TableComp>

      </LocalizationProvider>
    </>
  );
};

export default CompleteSessionDetail;
